import React, { useEffect, useLayoutEffect, useState, useRef, useCallback } from 'react';
import useIsMobile from './useIsMobile';
import useVisualViewport from './useVisualViewport';
import useChatStore from '../../stores/useChatStore';
import FlerpieChatHeader from './FlerpieChatHeader';
import FlerpieMessageThread from './FlerpieMessageThread';
import FlerpieInputBar from './FlerpieInputBar';
import FlerpieConversationSidebar from './FlerpieConversationSidebar';
import { FLERPIE_PANEL_ID, focusLauncher } from './flerpieDock';
import { FOCUS } from '../ui/motion';

const MIN_W = 340;
const MAX_W = 800;
const KEY_STEP = 16;

interface PanelState {
  isSidebarOpen: boolean;
  panelWidth: number;
  setPanelWidth: (width: number) => void;
}

// The page always keeps at least half the window.
const dockWidth = (w: number) => `min(${w}px, max(${MIN_W}px, 50vw))`;
const clampWidth = (w: number) => Math.round(Math.max(MIN_W, Math.min(MAX_W, window.innerWidth / 2, w)));

const FlerpieChatPanel: React.FC<{ closing?: boolean }> = ({ closing = false }) => {
  const isSidebarOpen = useChatStore((s: PanelState) => s.isSidebarOpen);
  const panelWidth = useChatStore((s: PanelState) => s.panelWidth);
  const setPanelWidth = useChatStore((s: PanelState) => s.setPanelWidth);
  const [mounted, setMounted] = useState(false);
  const [resizing, setResizing] = useState(false);
  const isMobile = useIsMobile();
  const { height: viewportHeight, offsetTop: viewportOffsetTop } = useVisualViewport();
  const panelRef = useRef<HTMLElement>(null);

  // Mount animation
  useEffect(() => {
    requestAnimationFrame(() => setMounted(true));
    return () => setMounted(false);
  }, []);

  // Visible = mounted AND not closing
  const visible = mounted && !closing;
  const docked = !isMobile && visible;

  // The page gives up the panel's width while it is docked (App.js <main>, DOCK_MOTION).
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (docked) {
      root.style.setProperty('--flerpie-dock', dockWidth(panelWidth));
      root.dataset.flerpieDock = resizing ? 'resizing' : 'open';
      root.dataset.flerpieSide = 'left';
    } else {
      root.style.removeProperty('--flerpie-dock');
      delete root.dataset.flerpieDock;
      delete root.dataset.flerpieSide;
    }
  }, [docked, panelWidth, resizing]);
  useLayoutEffect(() => () => {
    document.documentElement.style.removeProperty('--flerpie-dock');
    delete document.documentElement.dataset.flerpieDock;
    delete document.documentElement.dataset.flerpieSide;
  }, []);

  // Top sits on the nav's bottom edge, and climbs to 0 as the nav scrolls away.
  useLayoutEffect(() => {
    const el = panelRef.current;
    if (isMobile || !el) return undefined;
    // The nav is the App shell's direct child header; a page's own <header> is not.
    const header = document.querySelector('.App > header');
    const place = () => {
      el.style.top = `${header ? Math.max(0, header.getBoundingClientRect().bottom) : 0}px`;
    };
    place();
    window.addEventListener('scroll', place, { passive: true });
    window.addEventListener('resize', place);
    const ro = header && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(place) : null;
    if (ro && header) ro.observe(header);
    return () => {
      window.removeEventListener('scroll', place);
      window.removeEventListener('resize', place);
      if (ro) ro.disconnect();
    };
  }, [isMobile]);

  // Focus moves in on open (the input bar takes it when it can) and back to the launcher on close.
  useEffect(() => {
    const el = panelRef.current;
    if (isMobile || !el) return undefined;
    if (visible) {
      const id = requestAnimationFrame(() => {
        if (!el.contains(document.activeElement)) el.focus({ preventScroll: true });
      });
      return () => cancelAnimationFrame(id);
    }
    if (el.contains(document.activeElement)) focusLauncher();
    return undefined;
  }, [isMobile, visible]);

  // Escape closes the sidebar first, then the panel.
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Escape' || e.defaultPrevented) return;
    const s = useChatStore.getState();
    if (!s.isOpen) return;
    e.preventDefault();
    if (s.isSidebarOpen) s.toggleSidebar();
    else s.toggleOpen();
  };

  // --- Resize: drag the right edge, or arrow keys on it ---
  const onResizeStart = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = panelRef.current?.offsetWidth ?? panelWidth;
    setResizing(true);

    const onMove = (ev: PointerEvent) => setPanelWidth(clampWidth(startW + ev.clientX - startX));
    const onUp = () => {
      setResizing(false);
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, [panelWidth, setPanelWidth]);

  const onResizeKey = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    setPanelWidth(clampWidth(panelWidth + (e.key === 'ArrowRight' ? KEY_STEP : -KEY_STEP)));
  };

  // Mobile: full-screen overlay
  if (isMobile) {
    return (
      <div
        className={`fixed left-0 right-0 z-40 flex flex-col transition-[opacity,transform] duration-300 ease-out
                    ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        style={{
          top: viewportOffsetTop,
          height: viewportHeight,
          background: 'linear-gradient(180deg, #1a1a1a 0%, #141414 100%)',
        }}
      >
        <FlerpieChatHeader />
        <div className="relative flex-1 min-h-0">
          <FlerpieMessageThread />
        </div>
        {/* Sidebar overlay — rendered outside overflow-hidden so backdrop click works */}
        {isSidebarOpen && (
          <div className="absolute inset-0 z-30" style={{ top: 0 }}>
            <FlerpieConversationSidebar />
          </div>
        )}
        <div className="relative z-10">
          <FlerpieInputBar />
        </div>
        {/* Ambient glow at bottom */}
        <div
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-64 h-20 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse, rgba(255,204,51,0.06) 0%, transparent 70%)',
          }}
        />
      </div>
    );
  }

  // Desktop: docked left, from the nav's bottom edge to the window's.
  return (
    <aside
      ref={panelRef}
      id={FLERPIE_PANEL_ID}
      aria-label="Flerpie"
      tabIndex={-1}
      onKeyDown={onKeyDown}
      // The tail shadow is fill, not depth: the spring overshoots right, and the tail keeps the
      // window's left edge covered while it does.
      className={`fixed bottom-0 left-0 z-40 flex flex-col overflow-hidden bg-surface-container-low outline-none
                  shadow-[-24px_0_0_0_var(--surface-container-low)]
                  transition-[transform,opacity] motion-reduce:translate-x-0
                  ${visible
                    ? 'translate-x-0 opacity-100 duration-spring ease-spring'
                    : '-translate-x-full duration-exit ease-exit motion-reduce:opacity-0'}`}
      style={{ width: dockWidth(panelWidth) }}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize chat panel"
        aria-valuenow={panelWidth}
        aria-valuemin={MIN_W}
        aria-valuemax={MAX_W}
        tabIndex={0}
        onPointerDown={onResizeStart}
        onKeyDown={onResizeKey}
        className={`group absolute inset-y-0 right-0 z-50 w-1.5 cursor-ew-resize touch-none ${FOCUS}`}
      >
        <span
          aria-hidden="true"
          className={`absolute inset-y-0 right-0 w-0.5 bg-primary transition-opacity duration-quick ease-emphasized ${
            resizing ? 'opacity-40' : 'opacity-0 group-hover:opacity-40 group-focus-visible:opacity-40'
          }`}
        />
      </div>

      <FlerpieChatHeader />
      <div className="relative flex-1 min-h-0">
        {isSidebarOpen && <FlerpieConversationSidebar />}
        <FlerpieMessageThread />
      </div>
      <div className="relative z-30">
        <FlerpieInputBar />
      </div>
      {/* Ambient glow at bottom of panel */}
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-48 h-16 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse, rgba(255,204,51,0.04) 0%, transparent 70%)',
        }}
      />
    </aside>
  );
};

export default FlerpieChatPanel;
